import {
  type QueryClient,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import { getAllScreeners, restoreArchivedScreener } from "@/app/api/screeners";
import type {
  ArchivedScreenersTableQueryParams,
  ScreenersTableQueryParams,
} from "@/components/table/views/screeners/screeners-columns";
import { SCREENER_BUILDER_QUERY_KEYS } from "@/hooks/queries/screener-builder.queries";
import { useAuthedQuery } from "@/hooks/queries/use-authed-query.hook";
import { useScreenerBuilderEnabled } from "@/hooks/screener-builder-hooks";
import type { FilterParams } from "@/hooks/use-filter-params";
import type {
  AllScreenersResponse,
  ScreenerOrArchivedScreener,
} from "@/types/screeners";
import type { DefaultUseQueryConfigParam } from "@/types/third-party";

const SCREENER_QUERY_KEY_BASE = "screener";
export const SCREENER_QUERY_KEYS = {
  base: [SCREENER_QUERY_KEY_BASE] as const,
  list: (
    filters: Partial<
      ScreenersTableQueryParams | ArchivedScreenersTableQueryParams
    >,
  ) => [...SCREENER_QUERY_KEYS.base, filters] as const,
};

export const invalidateArchiveRestoreRelatedQueries = async (
  client: QueryClient,
  archivedScreenerId?: string,
) => {
  if (archivedScreenerId) {
    await client.invalidateQueries({
      queryKey:
        SCREENER_BUILDER_QUERY_KEYS.slimByScreenerId(archivedScreenerId),
    });
  }

  await client.invalidateQueries({ queryKey: SCREENER_QUERY_KEYS.base });
};

export const useAllScreenersQuery = (
  filterParams: FilterParams<
    ScreenersTableQueryParams | ArchivedScreenersTableQueryParams
  >,
  config: DefaultUseQueryConfigParam<
    AllScreenersResponse<ScreenerOrArchivedScreener>
  > = {},
) => {
  const builderEnabled = useScreenerBuilderEnabled();

  return useAuthedQuery({
    placeholderData: (previousData) => previousData,
    queryKey: SCREENER_QUERY_KEYS.list(filterParams.toObject()),
    queryFn: () => getAllScreeners(filterParams.toString()),
    select: ({ screenerRows, ...rest }) => ({
      ...rest,
      screenerRows: builderEnabled
        ? screenerRows
        : screenerRows.filter(({ isDraft }) => !isDraft),
    }),
    ...config,
  });
};

export const useRestoreArchivedScreenerMutation = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ screenerId }: { screenerId: string }) =>
      restoreArchivedScreener(screenerId),
    onSuccess: async () => await invalidateArchiveRestoreRelatedQueries(client),
  });
};
